import { computeStackedLayoutScaleForHeight } from '@src/utility/stackedLayout';

import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { Timeline } from '../models';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';
import { AddElementCommand } from './AddElementCommand';
import { UpdateElementCommand } from './UpdateElementCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export interface AddLayoutElementsParameters {
  layoutType: 'stacked-1-1' | 'stacked-1-3';
}

export class AddLayoutElementsCommand extends AbstractLayoutCommand {
  private params: AddLayoutElementsParameters;

  getName(): string {
    return 'AddLayoutElementsCommand';
  }
  private _topElement: Element | null = null;
  private _bottomElement: Element | null = null;

  constructor(
    editor: VideoEditor,
    element: Element,
    params: AddLayoutElementsParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.sourceElement?.id,
      params: this.params,
      extraElementData: this.extraElementData,
    });

    super.execute(); // makes a snapshot

    const highestLayoutTrack = this.getHighestLayoutTrack();

    const bottomElementTrack = this.editor.timeline.createTrack(
      'composition',
      String(highestLayoutTrack + 1),
    );
    const topElementTrack = this.editor.timeline.createTrack(
      'composition',
      String(highestLayoutTrack + 2),
    );

    const width = '100%';
    const x = '50%';
    const type = this.params.layoutType;

    const topHeight = type === 'stacked-1-1' ? '50%' : '33.333%';
    const bottomHeight = type === 'stacked-1-1' ? '50%' : '66.666%';
    const topScale = computeStackedLayoutScaleForHeight(topHeight);
    const bottomScale = computeStackedLayoutScaleForHeight(bottomHeight);

    const topElement = Timeline.createElement('composition', {
      time: this.sourceElement.time,
      duration: this.sourceElement.duration,
      x,
      y: type === 'stacked-1-1' ? '25%' : '16.666%',
      width,
      height: topHeight,
      volume: 0,
      clip: true,
      locked: false,
      z_index: -1000, // Force elements to appear behind regular tracks
      elements: [
        Timeline.createElement('video', {
          ...this.sourceElement.getState(),
          duration: null,
          time: 0,
          width: topScale,
          height: topScale,
          // Align LEFT for top split
          x: '0%',
          x_anchor: '0%',
          x_alignment: '0%',
          y: '50%',
          visible: true,
          locked: false,
          z_index: 0, // Ensure child elements have proper z-index
        }),
      ],
    });
    let command: Command = new AddElementCommand(
      this.editor,
      topElementTrack.id,
      topElement,
    );
    let success = this.editor.executeCommand(command);
    if (!success) {
      return false;
    }

    const bottomElement = Timeline.createElement('composition', {
      time: this.sourceElement.time,
      duration: this.sourceElement.duration,
      x,
      y: type === 'stacked-1-1' ? '75%' : '66.666%',
      width,
      height: bottomHeight,
      volume: 0,
      clip: true,
      locked: false,
      z_index: -1000, // Force elements to appear behind regular tracks
      elements: [
        Timeline.createElement('video', {
          ...this.sourceElement.getState(),
          duration: null,
          time: 0,
          width: bottomScale,
          height: bottomScale,
          // Align RIGHT for bottom split
          x: '100%',
          x_anchor: '100%',
          x_alignment: '100%',
          y: '50%',
          visible: true,
          locked: false,
          volume: '0', // Ensure child video has no volume
          z_index: 0, // Ensure child elements have proper z-index
        }),
      ],
    });
    command = new AddElementCommand(
      this.editor,
      bottomElementTrack.id,
      bottomElement,
    );
    success = this.editor.executeCommand(command);
    if (!success) {
      return false;
    }

    command = new UpdateElementCommand(this.editor, this.sourceElement.id, {
      locked: true,
      visible: false,
    });
    success = this.editor.executeCommand(command);
    if (!success) {
      return false;
    }

    this.extraElementData.setLayoutElements(
      this.sourceElement,
      topElement,
      bottomElement,
      this.params.layoutType,
    );

    this._topElement = topElement;
    this._bottomElement = bottomElement;
    this.canUndo = true;

    return true;
  }

  getDescription(): string {
    return `Entering layout mode`;
  }

  /**
   * @returns Current highest layout track number
   */
  private getHighestLayoutTrack(): number {
    // Use high track numbers for layout compositions to avoid conflicts
    const LAYOUT_TRACKS_BASE = 90; // Start layout tracks at track 90

    // Check if we already have layout composition tracks in use
    let highestLayoutTrack = LAYOUT_TRACKS_BASE - 1;

    // Find all existing compositions to identify layout tracks already in use
    for (const el of this.editor.timeline.allElements) {
      if (
        el.track != null &&
        this.extraElementData.isLayoutChild(el) &&
        el.track > highestLayoutTrack
      ) {
        highestLayoutTrack = el.track;
      }
    }

    return highestLayoutTrack;
  }

  getResultElements(): [Element, Element] | null {
    if (!this._topElement || !this._bottomElement) {
      return null;
    }
    return [this._topElement, this._bottomElement];
  }
}
